// @ts-nocheck - MultiGrid / cell renderer types
import React, { useState, useEffect, useCallback } from 'react';
import { useAppDispatch, useAppSelector } from '../../hooks';
import ActionBar from '../layout/ActionBar';
import { getKeyProviders } from '../../actions/keyProviderActions';
import { getAllTerritories } from '../../actions/territoryActions';
import { loadPromotions } from '../../actions/promotionActions';
import 'react-virtualized/styles.css';
import AutoSizer from 'react-virtualized/dist/commonjs/AutoSizer';
import MultiGrid from 'react-virtualized/dist/commonjs/MultiGrid';
import { hexToRgbA } from '../../util/formatter';
import { getSKUs } from '../../actions/skuActions';
import Loading from '../common/Loading';
import PercentageCell from './components/grid/cells/PercentageCell';
import EmptyCell from './components/grid/cells/EmptyCell';
import PriceCell from './components/grid/cells/PriceCell';
import GameNameCell from './components/grid/headers/GameNameCell';
import WarningCell from './components/grid/cells/WarningCell';
import GameNameHeaderCell from './components/grid/headers/GameNameHeaderCell';
import PriceHeaderCell from './components/grid/headers/PriceHeaderCell';
import PromotionHeaderCell from './components/grid/headers/PromotionHeaderCell';
import ExtraInfoHeaderCell from './components/grid/headers/ExtraInfoHeaderCell';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import SKUNameCell from './components/grid/headers/SKUNameCell';
import { usePageTitle } from '../../util/pageTitle';

export interface PromotionsGridProps {
  setClasses: (classes: string) => void;
}

function PromotionsGrid({ setClasses }: PromotionsGridProps) {
  const dispatch = useAppDispatch();
  const keyProviders = useAppSelector((state) => state.keyProviders);
  const territories = useAppSelector((state) => state.territories);
  const { t } = useTranslation();
  usePageTitle(['Promotions grid']);

  useEffect(() => {
    setClasses('flex-grow-1');
  }, [setClasses]);

  const [gridState, setGridState] = useState({
    scrollToColumn: 0,
    scrollToRow: 0,
  });

  const STYLE = {};
  const STYLE_BOTTOM_LEFT_GRID = {};
  const STYLE_TOP_LEFT_GRID = {
    fontWeight: 'bold',
  };
  const STYLE_TOP_RIGHT_GRID = {
    fontWeight: 'bold',
  };

  const [promotions, setPromotions] = useState<any>();
  const [games, setGames] = useState<any>();
  const [dateRange, setDateRange] = useState({
    start: new Date('2022-01-01'),
    end: new Date('2022-12-31'),
  });

  const mapGame = useCallback(
    (game: any) => ({
      gameId: game.gameId,
      gameName: game.gameName,
      isSKU: false,
      packshotUrl: game.packshotUrl,
      appId: game.appId,
      skuNames: game.skUs?.map((sku: any) => sku.skuName).flat(),
      packageIds: game.skUs?.map((sku: any) => sku.packageId).flat(),
      keyProviders: game.skUs?.map((sku: any) => sku.keyProviderId).flat(),
      platforms: game.skUs?.map((sku: any) => sku.platformId).flat(),
      territories: game.skUs
        ?.map((sku: any) =>
          sku.territories?.map((x: any) => territories?.find((y: any) => y.id === x))
        )
        .flat(),
    }),
    [territories]
  );

  const mapSKU = useCallback(
    (sku: any, game: any) => ({
      skuId: sku.skuId,
      skuName: sku.skuName,
      isSKU: true,
      gameId: game.gameId,
      gameName: game.gameName,
      appId: game.appId,
      packageId: sku.packageId,
      packageType: sku.packageType,
      platformId: sku.platformId,
      platform: sku.platform,
      platformImageId: sku.platformImageId,
      platformColourHex: sku.platformColourHex,
      keyProviderId: sku.keyProviderId,
      keyProviderName: sku.keyProviderName,
      keyProviderImageId: sku.keyProviderImageId,
      keyProviderColourHex: sku.keyProviderColourHex,
      territoriesRestricted: sku.territoriesRestricted,
      territories: sku.territories?.map((x: any) =>
        territories?.find((y: any) => y.id === x)
      ),
      promotions: [],
    }),
    [territories]
  );

  useEffect(() => {
    if (dateRange) {
      getSKUs().then((skus: any) => {
        let gamesList = skus.data.map((s: any) => ({
          gameName: s.gameName,
          gameId: s.gameId,
        }));
        gamesList = gamesList.filter(
          (v: any, i: number, a: any[]) => a.findIndex((t: any) => t.gameId === v.gameId) === i
        );

        const gameData = gamesList
          .map((game: any) => [
            mapGame(game),
            ...skus.data
              .filter((v: any) => v.gameId === game.gameId)
              .map((sku: any) => mapSKU(sku, game)),
          ])
          .flat();

        loadPromotions({
          filters: {
            promotionDateFrom: dateRange.start,
            promotionDateTo: dateRange.end,
          },
          _sort: 'start',
          _page: 0,
          _limit: 10000,
        })
          .then((promotionsRes: any) => {
            if (promotionsRes.data) {
              const promoData = promotionsRes.data.map((item: any, index: number) => ({
                name: item.name,
                id: item.id,
                start: item.start,
                end: item.end,
                color: item.keyProviderColourHex
                  ? hexToRgbA(item.keyProviderColourHex, 0.5)
                  : 'rgba(22,190,239,0.5)',
                games: item.games,
              }));

              promoData.forEach((promo: any) => {
                (promo.games || []).forEach((promoGame: any) => {
                  const game = gameData.find(
                    (g: any) => g.isSKU && g.skuId === promoGame.skuId
                  );
                  if (game && game.promotions) {
                    game.promotions.push({
                      id: promo.id,
                      keyValue: promoGame.keyValue,
                      discountPercentage: promoGame.discountPercentage,
                      price: promoGame.price,
                    });
                  }
                });
              });

              setGames(gameData);
              setPromotions(promoData);
            }
          })
          .catch((e: any) => {
            toast.error(e.message ? e.message : t('Failed to get promotions'));
          });
      });
    }
  }, [dateRange, mapGame, mapSKU, t]);

  useEffect(() => {
    if (!keyProviders) {
      dispatch(getKeyProviders());
    }
  }, [keyProviders, dispatch]);

  useEffect(() => {
    if (!territories) {
      dispatch(getAllTerritories());
    }
  }, [territories, dispatch]);

  const _cellRenderer = ({
    columnIndex,
    key,
    parent,
    rowIndex,
    style,
  }: {
    columnIndex: number;
    key: string;
    parent: any;
    rowIndex: number;
    style: any;
  }) => {
    let content = <div></div>;

    if (rowIndex === 0) {
      if (columnIndex === 0) {
        content = <GameNameHeaderCell />;
      } else if (columnIndex === 1) {
        content = <PriceHeaderCell />;
      } else if (columnIndex === 2) {
        content = <ExtraInfoHeaderCell />;
      } else if (promotions) {
        content = (
          <PromotionHeaderCell promotion={promotions[columnIndex - 3]} />
        );
      }
    } else if (games && promotions) {
      const game = games[rowIndex - 1];
      if (!game.isSKU) {
        if (columnIndex === 0) {
          content = (
            <GameNameCell
              gameId={game.gameId}
              gameName={game.gameName}
              skuName={game.skuName}
            />
          );
        } else {
          content = <EmptyCell />;
        }
      } else {
        const promo = promotions[columnIndex - 3];
        const gamePromo = game.promotions?.find((x: any) => x.id === promo?.id);
        if (columnIndex === 0) {
          content = (
            <SKUNameCell
              promotion={gamePromo}
              name={game.skuName}
              id={game.skuId}
            />
          );
        } else if (columnIndex === 1) {
          content = <PriceCell promotion={gamePromo} game={game} />;
        } else if (columnIndex === 2) {
          content = <EmptyCell />;
        } else if (columnIndex === 3) {
          content = (
            <PercentageCell promotion={{ discount: 0.5 }} game={game} />
          );
        } else if (columnIndex === 4) {
          content = <EmptyCell />;
        } else if (columnIndex === 5) {
          content = <WarningCell />;
        } else if (columnIndex === 6) {
          content = (
            <PercentageCell
              promotion={{ discount: 0.5 }}
              game={game}
              editable={true}
            />
          );
        } else if (gamePromo) {
          content = <PercentageCell promotion={gamePromo} game={game} />;
        } else {
          content = <EmptyCell />;
        }
      }
    }

    return (
      <div key={key} style={{ ...style, overflow: 'hidden' }}>
        {content}
      </div>
    );
  };

  const _getColumnWidth = ({ index }: { index: number }) => {
    switch (index) {
      case 0:
        return 300;
      case 1:
        return 100;
      case 2:
        return 100;
      default:
        return 150;
    }
  };

  const _getRowHeight = ({ index }: { index: number }) => {
    if (index === 0) {
      return 120;
    }
    return 39;
  };

  return promotions && games ? (
    <>
      <ActionBar
        breadcrumb={[
          {
            link: '/promotions/grid',
            label: t('Promotion explorer'),
          },
        ]}
      />
      <div className="grid-wrapper promo-grid">
        <AutoSizer>
          {({ width, height }) => (
            <MultiGrid
              {...gridState}
              fixedColumnCount={3}
              fixedRowCount={1}
              cellRenderer={_cellRenderer}
              columnWidth={_getColumnWidth}
              columnCount={promotions.length + 3}
              enableFixedColumnScroll
              enableFixedRowScroll
              height={height}
              rowHeight={_getRowHeight}
              rowCount={games.length + 1}
              style={STYLE}
              styleBottomLeftGrid={STYLE_BOTTOM_LEFT_GRID}
              styleTopLeftGrid={STYLE_TOP_LEFT_GRID}
              styleTopRightGrid={STYLE_TOP_RIGHT_GRID}
              width={width}
              hideTopRightGridScrollbar
              hideBottomLeftGridScrollbar
            />
          )}
        </AutoSizer>
      </div>
    </>
  ) : (
    <Loading />
  );
}

export default PromotionsGrid;
